<template>
  <div class="card mt-4 mb-4">
    <!-- Cabecera -->
    <div class="card-header pb-2">
      <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h5 class="mb-1 text-success">Asistencia · Recreativo Infantil</h5>
          <span class="badge bg-gradient-success">
            <i class="fas fa-user-check me-1" aria-hidden="true"></i>
            {{ entrances.total || 0 }} {{ reportLabel.toLowerCase() }} en el periodo
          </span>
        </div>
      </div>
    </div>

    <!-- Filtros en una sola fila -->
    <div class="card-body py-3 border-bottom">
      <div class="row g-3 align-items-end">
        <div class="col-12 col-md">
          <label class="form-label text-xs text-secondary mb-1">Fecha</label>
          <DatePicker :value="date" @change-date="handlerDate" />
        </div>
        <div class="col-6 col-md-auto">
          <label for="inf-report" class="form-label text-xs text-secondary mb-1">Tipo de reporte</label>
          <select id="inf-report" v-model="report" class="form-select" @change="resetAndFetch">
            <option value="attendees">Asistentes</option>
            <option value="absentees">No asistentes</option>
          </select>
        </div>
        <div class="col-6 col-md-auto">
          <label for="inf-team" class="form-label text-xs text-secondary mb-1">Equipo</label>
          <select id="inf-team" v-model="selectedTeamId" class="form-select" @change="resetAndFetch">
            <option value="">Todos</option>
            <option v-for="team in teams" :key="team.id" :value="team.id">{{ team.name }}</option>
          </select>
        </div>
        <div class="col-12 col-md-auto">
          <div class="dropdown">
            <button
              class="btn bg-gradient-success mb-0 w-100"
              type="button"
              aria-label="Opciones de descarga"
              :aria-expanded="showDownloadMenu"
              @click.stop="toggleDownloadMenu"
            >
              <i class="fas fa-download me-1" aria-hidden="true"></i> Descargar
              <i class="fas fa-caret-down ms-1" aria-hidden="true"></i>
            </button>
            <ul class="dropdown-menu dropdown-menu-end px-2 py-3" :class="{ show: showDownloadMenu }">
              <li>
                <a href="javascript:;" class="dropdown-item border-radius-md" @click.stop.prevent="downloadExport('excel')">
                  <i class="fas fa-file-excel text-success me-2" aria-hidden="true"></i> Descargar Excel
                </a>
              </li>
              <li>
                <a href="javascript:;" class="dropdown-item border-radius-md" @click.stop.prevent="downloadExport('pdf')">
                  <i class="fas fa-file-pdf text-danger me-2" aria-hidden="true"></i> Descargar PDF
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div v-if="hasActiveFilters" class="col-12 col-md-auto">
          <button class="btn btn-outline-secondary mb-0 w-100" @click.stop="clearFilters">
            <i class="fas fa-times me-1" aria-hidden="true"></i> Limpiar filtros
          </button>
        </div>
      </div>
    </div>

    <!-- Tabla / estados -->
    <div class="card-body px-0 pt-0 pb-2">
      <div class="table-responsive p-0">
        <table class="table align-items-center mb-0">
          <thead>
            <tr>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-4">Nombre</th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Edad</th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Equipo</th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">{{ report === 'absentees' ? 'Estado' : 'Hora' }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="isLoading">
              <tr v-for="n in 6" :key="'sk' + n">
                <td class="ps-4"><div class="skeleton" style="width: 60%"></div></td>
                <td><div class="skeleton" style="width: 30%"></div></td>
                <td><div class="skeleton" style="width: 40%"></div></td>
                <td><div class="skeleton" style="width: 40%"></div></td>
              </tr>
            </template>
            <template v-else-if="entranceRows.length">
              <tr v-for="(entrance, index) in entranceRows" :key="entrance.id || index">
                <td class="ps-4">
                  <a v-if="rowUserId(entrance)" :href="`/recreativo-infantil/${rowUserId(entrance)}`" target="_blank">
                    <h6 class="mb-0 text-sm">{{ rowUserName(entrance) }}</h6>
                  </a>
                  <h6 v-else class="mb-0 text-sm">{{ rowUserName(entrance) }}</h6>
                </td>
                <td>
                  <span class="text-sm text-secondary">{{ rowAge(entrance) }}</span>
                </td>
                <td>
                  <span class="text-sm text-secondary">{{ rowTeamName(entrance) }}</span>
                </td>
                <td>
                  <span class="text-sm text-primary font-weight-bold">{{ entrance.created_at ? formatDate(entrance.created_at) : 'Sin asistencia' }}</span>
                </td>
              </tr>
            </template>
          </tbody>
        </table>

        <div v-if="!isLoading && !entranceRows.length" class="text-center py-5">
          <i class="fas fa-inbox text-success" style="font-size: 2.6rem; opacity: .35;" aria-hidden="true"></i>
          <p class="mt-3 mb-0 text-secondary text-sm">No hay {{ reportLabel.toLowerCase() }} para el periodo seleccionado.</p>
        </div>
      </div>

      <div v-if="!isLoading && entranceRows.length" class="d-flex flex-wrap justify-content-end align-items-center gap-2 px-4 py-3">
        <label for="inf-per-page" class="me-2 text-sm mb-0">Mostrar:</label>
        <select id="inf-per-page" v-model="perPage" @change="resetAndFetch" class="form-select form-select-sm w-auto me-3">
          <option value="10">10</option>
          <option value="20">20</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </select>
        <button @click="changePage('prev')" :disabled="!entrances.prev_page_url" class="btn btn-outline-success btn-sm mb-0">Anterior</button>
        <span class="text-sm mx-1">Página {{ entrances.current_page }} de {{ entrances.last_page }}</span>
        <button @click="changePage('next')" :disabled="!entrances.next_page_url" class="btn btn-outline-success btn-sm mb-0">Siguiente</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import es from "moment/dist/locale/es";
import DatePicker from "../../../components/DatePicker.vue";
moment.updateLocale("es", es);
const PUBLIC_API_URL = process.env.VUE_APP_PUBLIC_API_URL;

export default {
  components: { DatePicker },
  data() {
    return {
      isLoading: false,
      entrances: { data: [] },
      currentPage: 1,
      perPage: 50,
      user_type: "Infant",
      date: { start: new Date(), end: new Date() },
      report: "attendees",
      showDownloadMenu: false,
      requestController: null,
      selectedTeamId: "",
      teams: [],
    };
  },
  computed: {
    entranceRows() {
      if (!this.entrances || !this.entrances.data) return [];
      const rows = Array.isArray(this.entrances.data)
        ? this.entrances.data
        : Object.values(this.entrances.data);

      return rows.filter((row) => !row.user_type || row.user_type.endsWith("\\Infant"));
    },
    reportLabel() {
      return this.report === "absentees" ? "No asistentes" : "Asistentes";
    },
    hasActiveFilters() {
      const today = this.formatDateForApi(new Date());
      const start = this.formatDateForApi(this.date.start);
      const end = this.formatDateForApi(this.date.end);
      return this.selectedTeamId !== "" || this.report !== "attendees" || start !== today || end !== today;
    },
  },
  mounted() {
    document.addEventListener("click", this.closeDownloadMenu);
  },
  beforeUnmount() {
    if (this.requestController) {
      this.requestController.abort();
    }
    document.removeEventListener("click", this.closeDownloadMenu);
  },
  created() {
    this.fetchTeams();
    this.fetchEntrances();
  },
  methods: {
    toggleDownloadMenu() {
      this.showDownloadMenu = !this.showDownloadMenu;
    },
    closeDownloadMenu() {
      this.showDownloadMenu = false;
    },
    async fetchTeams() {
      try {
        const response = await axios.get(`${PUBLIC_API_URL}/teams-infants`, {
          params: { include_inactive: 1 },
        });
        this.teams = response.data;
      } catch (error) {
        this.teams = [];
      }
    },
    handlerDate(date) {
      const normalized = this.normalizeDateRange(date);
      if (!normalized) return;
      if (this.isSameDateRange(this.date, normalized)) return;
      this.date = normalized;
      this.resetAndFetch();
    },
    resetAndFetch() {
      this.currentPage = 1;
      this.fetchEntrances();
    },
    clearFilters() {
      this.selectedTeamId = "";
      this.report = "attendees";
      this.date = { start: new Date(), end: new Date() };
      this.resetAndFetch();
    },
    async fetchEntrances() {
      if (this.requestController) {
        this.requestController.abort();
      }

      const controller = new AbortController();
      this.requestController = controller;

      try {
        this.isLoading = true;
        const range = this.normalizeDateRange(this.date) || {
          start: new Date(),
          end: new Date(),
        };
        const params = {
          page: this.currentPage,
          per_page: this.perPage,
          user_type: this.user_type,
          report: this.report,
          team_id: this.selectedTeamId || undefined,
          start_date: this.formatDateForApi(range.start),
          end_date: this.formatDateForApi(range.end),
        };
        const response = await axios.get(`${PUBLIC_API_URL}/entrances`, {
          params,
          signal: controller.signal,
        });
        this.entrances = response.data;
      } catch (error) {
        if (axios.isCancel(error) || error.name === "CanceledError") return;
        this.entrances = { data: [] };
      } finally {
        if (this.requestController === controller) {
          this.requestController = null;
          this.isLoading = false;
        }
      }
    },
    changePage(direction) {
      if (direction === "next" && this.entrances.next_page_url) this.currentPage++;
      else if (direction === "prev" && this.entrances.prev_page_url) this.currentPage--;
      this.fetchEntrances();
    },
    downloadExport(format) {
      this.showDownloadMenu = false;
      const range = this.normalizeDateRange(this.date) || {
        start: new Date(),
        end: new Date(),
      };
      const params = new URLSearchParams({
        report: this.report,
        format: format,
        scope: "range",
        start_date: this.formatDateForApi(range.start),
        end_date: this.formatDateForApi(range.end),
      });
      if (this.selectedTeamId) {
        params.append("team_id", this.selectedTeamId);
      }
      const link = document.createElement("a");
      link.href = `${PUBLIC_API_URL}/export/infants?${params.toString()}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    formatDate(date) {
      return moment(moment.utc(date).toDate()).format("LLLL");
    },
    formatDateForApi(date) {
      return moment(date).format("YYYY-MM-DD");
    },
    rowUser(row) {
      return row.user || row;
    },
    rowUserId(row) {
      return this.rowUser(row)?.id;
    },
    rowUserName(row) {
      const user = this.rowUser(row);
      return `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "Sin nombre";
    },
    rowAge(row) {
      const age = this.rowUser(row)?.age;
      return age || age === 0 ? age : "—";
    },
    rowTeamName(row) {
      const team = this.rowUser(row)?.teams?.[0];
      return team?.name || "Sin equipo";
    },
    normalizeDateRange(value) {
      if (!value || !value.start) return null;

      const start = moment(value.start);
      const end = moment(value.end || value.start);

      if (!start.isValid() || !end.isValid()) return null;

      return {
        start: start.toDate(),
        end: end.toDate(),
      };
    },
    isSameDateRange(firstRange, secondRange) {
      const first = this.normalizeDateRange(firstRange);
      const second = this.normalizeDateRange(secondRange);

      if (!first || !second) return false;

      return this.formatDateForApi(first.start) === this.formatDateForApi(second.start)
        && this.formatDateForApi(first.end) === this.formatDateForApi(second.end);
    },
  },
};
</script>

<style scoped>
.skeleton {
  height: 14px;
  border-radius: 6px;
  background: linear-gradient(90deg, #edf0f3 25%, #f7f9fb 37%, #edf0f3 63%);
  background-size: 400% 100%;
  animation: shimmer 1.4s ease infinite;
}
@keyframes shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: 0 0; }
}
</style>
